/**
 * "What a phone plays" (splash-cms §1 part 8, SP-D25): which splash a phone would play when it
 * opens the app at a given moment, under the panel's stated assumption — a phone that opens the
 * app once a day, at the current time of day.
 *
 * - A switched-on splash of this app whose window `[startsAt, endsAt)` contains the moment plays
 *   (phones download splashes due in the next 14 days early, SP-D7)…
 * - …except on the Dubai day the splash was created: the phone only fetches it after that day's
 *   open, so that open plays the built-in (the 2nd-open rule, SP-D4).
 * - Otherwise the built-in plays; a splash due within 14 days is already downloaded.
 */
import { dubaiDayKey, formatDubaiDateTime } from "@/lib/dubaiDate";

export const SPLASH_PANEL_ASSUMPTION = "Assumes a phone that opens the app once a day, at this time of day.";

const DAY_MS = 86_400_000;
export const DOWNLOAD_AHEAD_DAYS = 14;

export interface PhoneSplash {
  id?: string;
  name?: string | null;
  appScheme?: string | null;
  isActive?: boolean;
  startsAt?: string;
  endsAt?: string;
  createdAt?: string;
}

export type PhonePlays<T extends PhoneSplash = PhoneSplash> =
  | { plays: "splash"; splash: T }
  | { plays: "builtin"; reason: "none" }
  | { plays: "builtin"; reason: "downloaded" | "created-today"; splash: T };

/** The phone's open `days` days after `now`, at the same time of day. */
export function phoneOpenMoment(now: Date, days: number): Date {
  return new Date(now.getTime() + days * DAY_MS);
}

export function whatPhonePlays<T extends PhoneSplash>(splashes: readonly T[], moment: Date, appScheme: string): PhonePlays<T> {
  const at = moment.getTime();
  const candidates = splashes
    .filter((s) => s.isActive === true && s.appScheme === appScheme)
    .map((s) => ({ splash: s, start: Date.parse(s.startsAt ?? ""), end: Date.parse(s.endsAt ?? "") }))
    .filter((c) => Number.isFinite(c.start) && Number.isFinite(c.end))
    .sort((a, b) => a.start - b.start);

  const live = candidates.find((c) => c.start <= at && at < c.end);
  if (live) {
    const createdToday = live.splash.createdAt !== undefined && dubaiDayKey(new Date(live.splash.createdAt)) === dubaiDayKey(moment);
    return createdToday ? { plays: "builtin", reason: "created-today", splash: live.splash } : { plays: "splash", splash: live.splash };
  }

  const upcoming = candidates.find((c) => c.start > at && c.start - at <= DOWNLOAD_AHEAD_DAYS * DAY_MS);
  return upcoming ? { plays: "builtin", reason: "downloaded", splash: upcoming.splash } : { plays: "builtin", reason: "none" };
}

const BUILT_IN = "The built-in splash";

/** What plays, and one sentence why — dates in Dubai time. */
export function explainPhonePlays(result: PhonePlays): { plays: string; why: string } {
  if (result.plays === "splash") {
    const { splash } = result;
    return {
      plays: splash.name ?? "",
      why: `Inside its dates (${formatDubaiDateTime(splash.startsAt)} – ${formatDubaiDateTime(splash.endsAt)}).`,
    };
  }
  if (result.reason === "none") {
    return { plays: BUILT_IN, why: `No occasion splash is on for this day or due in the next ${DOWNLOAD_AHEAD_DAYS} days.` };
  }
  const name = result.splash.name ?? "";
  if (result.reason === "downloaded") {
    return {
      plays: BUILT_IN,
      why: `${name} starts ${formatDubaiDateTime(result.splash.startsAt)}. The phone has already downloaded it, so it plays from its first open that day.`,
    };
  }
  return {
    plays: BUILT_IN,
    why: `${name} was added today. The phone downloads it after today's open and plays it from the next open.`,
  };
}
